/**
 * EmojiReactions - Shows floating emojis when reactions are sent
 * The reaction picker is now in the Chat component
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../../stores/gameStore';
import { getPlayerColor } from '../../utils/playerColors';

export const REACTION_EMOJIS = ['👏', '🔥', '😱', '💀', '🎉', '👀'];

interface FloatingEmoji {
  id: string;
  emoji: string;
  x: number;
  y: number;
  playerName?: string;
  colorIndex?: number;
}

export function EmojiReactions() {
  const chatMessages = useGameStore((s) => s.chatMessages);
  const [floatingEmojis, setFloatingEmojis] = useState<FloatingEmoji[]>([]);
  // Track the last processed message by id (array length breaks at the 100-message cap).
  // Initialised to the current last message so history isn't replayed on mount.
  const lastProcessedIdRef = useRef<string | null>(
    useGameStore.getState().chatMessages.at(-1)?.id ?? null
  );
  const timeoutsRef = useRef<Set<number>>(new Set());

  // Listen for reaction chat messages
  useEffect(() => {
    if (chatMessages.length === 0) {
      lastProcessedIdRef.current = null;
      return;
    }

    // Collect messages newer than the last processed one
    const lastIdx = lastProcessedIdRef.current
      ? chatMessages.findIndex((m) => m.id === lastProcessedIdRef.current)
      : -1;
    const newMessages = chatMessages.slice(lastIdx + 1);
    lastProcessedIdRef.current = chatMessages[chatMessages.length - 1].id;

    const newEmojis: FloatingEmoji[] = [];
    for (const msg of newMessages) {
      if (msg.isSystem) continue;
      // Check if it's a reaction message (just an emoji)
      const text = msg.text.trim();
      if (!REACTION_EMOJIS.includes(text)) continue;
      newEmojis.push({
        id: msg.id,
        emoji: text,
        x: 30 + Math.random() * 40, // Random position 30-70% from left
        y: 30 + Math.random() * 30, // Random position 30-60% from top
        playerName: msg.playerName,
        colorIndex: msg.colorIndex,
      });
    }
    if (newEmojis.length === 0) return;

    setFloatingEmojis((prev) => [...prev, ...newEmojis]);

    // Remove after animation
    const ids = new Set(newEmojis.map((e) => e.id));
    const timeoutId = window.setTimeout(() => {
      timeoutsRef.current.delete(timeoutId);
      setFloatingEmojis((prev) => prev.filter((e) => !ids.has(e.id)));
    }, 2000);
    timeoutsRef.current.add(timeoutId);
  }, [chatMessages]);

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
      timeouts.clear();
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <AnimatePresence>
        {floatingEmojis.map((emoji) => {
          const color = emoji.colorIndex !== undefined
            ? getPlayerColor(emoji.colorIndex)
            : null;
          return (
            <motion.div
              key={emoji.id}
              initial={{
                left: `${emoji.x}%`,
                top: `${emoji.y}%`,
                opacity: 0,
                scale: 0.5,
              }}
              animate={{
                opacity: 1,
                scale: 1.5,
                y: -50,
              }}
              exit={{
                opacity: 0,
                scale: 0.5,
                y: -100,
              }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
              className="absolute text-4xl"
            >
              {emoji.emoji}
              {emoji.playerName && (
                <span
                  className={`absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap ${color?.text || 'text-slate-500'}`}
                >
                  {emoji.playerName}
                </span>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
